import { useState, type ReactNode } from "react";
import {
  PLAN_EDIT_LIMITS,
  capturedRooms,
  planEditKey,
  surfacePlanLine,
  planLineLengthM,
  type PlanRoom,
  type PlanSegment,
  type SkippedPlanEdit,
} from "@sts/geometry";
import { formatLength } from "@sts/scan-viewer";
import { editedByLine, openingOffsetM, openingSillM, recordFor } from "./plan-editor.js";
import type { PlanEditLayer } from "./use-plan-edits.js";
import type { LengthAnchor, PlanEditor } from "./use-plan-editor.js";
import { HowToLink } from "../help/HowToLink.jsx";

/**
 * The plan editor's side panel: the tools, undo and redo, and the selected
 * wall, opening or room as fields — the exact-figure and keyboard path to
 * everything the drag handles do, and where "Edited by … on …" and "Revert to
 * scan" live.
 *
 * Every figure typed here is checked against `PLAN_EDIT_LIMITS` — the bounds
 * the API holds corrections to — before it is sent, so a typo is caught here
 * rather than refused there.
 */

const L = PLAN_EDIT_LIMITS;

function parseMetres(raw: string): number | null {
  const text = raw.trim().replace(",", ".").replace(/\s*m$/i, "");
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
}

function NumberField({
  label,
  value,
  unit,
  min,
  max,
  scale = 1,
  digits = 2,
  placeholder,
  onCommit,
  testId,
}: {
  readonly label: string;
  /** In metres (or the field's own unit when `scale` is 1). */
  readonly value: number | undefined;
  readonly unit: string;
  readonly min: number;
  readonly max: number;
  /** Shown value = value × scale: 1000 shows metres as millimetres. */
  readonly scale?: number;
  readonly digits?: number;
  readonly placeholder?: string;
  readonly onCommit: (metres: number) => void;
  readonly testId?: string;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const shown = value === undefined ? "" : (value * scale).toFixed(digits);
  return (
    <label className="floor-plan__field floor-plan__edit-field">
      <span>
        {label} <span className="muted">({unit})</span>
      </span>
      <input
        inputMode="decimal"
        data-testid={testId}
        key={shown}
        defaultValue={shown}
        placeholder={placeholder}
        aria-invalid={error !== null}
        onBlur={(event) => {
          const n = parseMetres(event.target.value);
          if (n === null || event.target.value.trim() === shown) {
            setError(null);
            return;
          }
          const metres = (n as number) / scale;
          if (!Number.isFinite(metres) || metres < min || metres > max) {
            setError(`Between ${(min * scale).toFixed(scale === 1 ? 2 : 0)} and ${(max * scale).toFixed(0)} ${unit}.`);
            return;
          }
          setError(null);
          onCommit(metres);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.currentTarget.value = shown;
            setError(null);
            event.currentTarget.blur();
          }
        }}
      />
      {error ? <em className="floor-plan__field-error">{error}</em> : null}
    </label>
  );
}

export interface PlanEditPanelProps {
  readonly placement: "side" | "below";
  readonly editor: PlanEditor;
  readonly layer: PlanEditLayer;
  /** This storey's rooms as drawn. */
  readonly rooms: readonly PlanRoom[];
  readonly activeStory: number;
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string | undefined) => void;
}

export function PlanEditPanel({
  placement,
  editor,
  layer,
  rooms,
  activeStory,
  selectedId,
  onSelect,
}: PlanEditPanelProps): ReactNode {
  const records = layer.state.records;
  const segments = rooms.flatMap((room) => room.segments.map((segment) => ({ segment, room })));
  const selected = segments.find((s) => s.segment.id === selectedId);
  const selectedRoom = selected ? undefined : rooms.find((room) => selectedId && room.floorIds.includes(selectedId));
  const deleted = records.filter((r) => r.action === "delete" && r.storey === activeStory);
  const skipped = layer.plan.skipped.filter((s) => s.edit.storey === activeStory);
  // Rooms on this storey whose walls were corrected but whose floor could not
  // be redrawn to them: their area is still the scan's, and the panel says so.
  const staleFloors = layer.plan.floors.flatMap((f) => {
    if (f.outcome !== "as-scanned") return [];
    const room = rooms.find((r) => r.externalRef === f.roomRef);
    return room ? [{ room, reason: f.reason }] : [];
  });
  const labelOfDeleted = new Map(
    capturedRooms(layer.capture).flatMap((room) => [
      ...(room.walls ?? []).map((w, i) => [w.identifier, `Wall ${i + 1}`] as const),
      ...(room.windows ?? []).map((w, i) => [w.identifier, `Window ${i + 1}`] as const),
      ...(room.doors ?? []).map((w, i) => [w.identifier, `Door ${i + 1}`] as const),
      ...(room.openings ?? []).map((w, i) => [w.identifier, `Opening ${i + 1}`] as const),
    ]),
  );

  const tool = editor.tool;
  const hint = tool
    ? tool.kind === "wall"
      ? editor.pendingStart
        ? "Click where the wall ends. Enter puts it there; Esc takes back its start."
        : "Click where the new wall starts. Ends snap to corners, then to walls."
      : tool.kind === "split"
        ? editor.pendingStart
          ? "Click on the wall across the room where the line ends. The room becomes two, with a wall between them."
          : "Click on a wall where the line dividing the room starts. Esc puts the tool down."
        : `Click a wall to put a ${tool.opening} in it. Enter puts it where the pointer is; Esc puts the tool down.`
    : selected || selectedRoom
      ? null
      : "Drag a wall to move it (its corners stay joined; Alt moves it alone, Shift in any direction). Select a wall for its end handles, exact length, thickness and height; drag a door or window along its wall. Select a floor to rename, merge or split the room.";

  // Rooms split or merged on this storey — each with its way back.
  const structure = records.flatMap((r) => {
    if (r.targetKind !== "room" || r.action !== "modify" || r.storey !== activeStory) return [];
    const data = r.data as { readonly label?: string; readonly split?: unknown; readonly mergeInto?: string };
    const name = data.label ?? "A room";
    if (data.split !== undefined) return [{ ref: r.targetId, words: `${name} split in two`, name }];
    if (data.mergeInto !== undefined) {
      const into = rooms.find((room) => room.externalRef === data.mergeInto)?.label ?? "another room";
      return [{ ref: r.targetId, words: `${name} merged into ${into}`, name }];
    }
    return [];
  });

  return (
    <section
      className={`floor-plan__panel floor-plan__panel--${placement} floor-plan__edit-panel`}
      aria-label="Edit plan"
      data-testid="plan-edit-panel"
      style={{ minWidth: 0 }}
    >
      <p className="floor-plan__panel-title floor-plan__panel-title--with-help">
        Edit plan <HowToLink guide="floor-plan">How to edit the plan</HowToLink>
      </p>

      <div className="floor-plan__edit-tools" role="group" aria-label="Add to the plan">
        <button type="button" className="floor-plan__tool" aria-pressed={tool?.kind === "wall"} onClick={() => editor.armTool(tool?.kind === "wall" ? null : { kind: "wall" })}>
          Add wall
        </button>
        <button
          type="button"
          className="floor-plan__tool"
          aria-pressed={tool?.kind === "split"}
          onClick={() => editor.armTool(tool?.kind === "split" ? null : { kind: "split" })}
        >
          Split room
        </button>
        {(["door", "window", "opening"] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            className="floor-plan__tool"
            aria-pressed={tool?.kind === "opening" && tool.opening === kind}
            onClick={() =>
              editor.armTool(tool?.kind === "opening" && tool.opening === kind ? null : { kind: "opening", opening: kind })
            }
          >
            {kind === "door" ? "Door" : kind === "window" ? "Window" : "Opening"}
          </button>
        ))}
      </div>

      <div className="floor-plan__edit-history" role="group" aria-label="Undo and redo">
        <button
          type="button"
          className="floor-plan__tool"
          disabled={!editor.canUndo}
          onClick={editor.undo}
          title={editor.undoLabel ? `Undo ${editor.undoLabel} (Ctrl/Cmd+Z)` : "Nothing to undo"}
        >
          Undo
        </button>
        <button
          type="button"
          className="floor-plan__tool"
          disabled={!editor.canRedo}
          onClick={editor.redo}
          title={editor.redoLabel ? `Redo ${editor.redoLabel} (Ctrl/Cmd+Shift+Z)` : "Nothing to redo"}
        >
          Redo
        </button>
      </div>

      {hint ? (
        <p className="floor-plan__panel-hint" role="status">
          {hint}
        </p>
      ) : null}

      {layer.state.error ? (
        <p className="floor-plan__panel-error" role="alert">
          {layer.state.error}{" "}
          <button type="button" className="floor-plan__link" onClick={layer.state.dismissError}>
            Dismiss
          </button>
        </p>
      ) : null}
      {editor.notice ? (
        <p className="floor-plan__edit-notice" role="status" data-testid="plan-edit-notice">
          {editor.notice}
        </p>
      ) : null}

      {selected ? (
        selected.segment.kind === "wall" ? (
          <WallCard key={selected.segment.id} wall={selected.segment} room={selected.room} editor={editor} layer={layer} />
        ) : (
          <OpeningCard
            key={selected.segment.id}
            opening={selected.segment}
            host={segments.find((s) => s.segment.id === selected.segment.parentId)?.segment}
            editor={editor}
            layer={layer}
          />
        )
      ) : selectedRoom ? (
        <RoomCard key={selectedRoom.index} room={selectedRoom} rooms={rooms} editor={editor} layer={layer} />
      ) : null}

      {structure.length > 0 ? (
        <div className="floor-plan__edit-deleted">
          <p className="floor-plan__edit-subtitle">Rooms split or merged on this storey</p>
          <ul className="floor-plan__panel-list">
            {structure.map((s) => (
              <li key={s.ref} className="floor-plan__edit-row">
                <span>{s.words}</span>
                <button type="button" className="floor-plan__link" onClick={() => editor.undoRoomStructure(s.ref, s.words)}>
                  Undo
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {deleted.length > 0 ? (
        <div className="floor-plan__edit-deleted">
          <p className="floor-plan__edit-subtitle">Deleted on this storey</p>
          <ul className="floor-plan__panel-list">
            {deleted.map((r) => (
              <li key={planEditKey(r)} className="floor-plan__edit-row">
                <span>
                  {labelOfDeleted.get(r.targetId) ?? r.targetId}
                  <span className="muted small"> · {editedByLine(r)}</span>
                </span>
                <button
                  type="button"
                  className="floor-plan__link"
                  onClick={() => {
                    editor.revert(r.targetKind, r.targetId, `restore ${labelOfDeleted.get(r.targetId) ?? "element"}`);
                    onSelect(r.targetId);
                  }}
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {staleFloors.length > 0 ? (
        <div className="floor-plan__edit-skipped" role="note" data-testid="plan-edit-floors">
          <p className="floor-plan__edit-subtitle">
            {staleFloors.length === 1 ? "A floor is not redrawn" : `${staleFloors.length} floors are not redrawn`}
          </p>
          <ul className="floor-plan__panel-list">
            {staleFloors.map(({ room, reason }) => (
              <li key={room.index} className="floor-plan__edit-row small">
                {room.label}: {reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {skipped.length > 0 ? <Skipped skipped={skipped} editor={editor} /> : null}

      <p className="floor-plan__panel-note">
        Corrections are <strong>saved with the survey</strong> and seen by the whole team. The scan itself is kept
        exactly as captured: <em>Revert to scan</em> takes a correction away. The heat loss follows them; anything a
        person entered or confirmed in the survey is kept.
      </p>
    </section>
  );
}

function Status({ layer, kind, id }: { readonly layer: PlanEditLayer; readonly kind: "wall" | "opening" | "room"; readonly id: string }): ReactNode {
  const record = recordFor(layer.state.records, kind, id);
  return (
    <p className="floor-plan__edit-status" data-testid="plan-edit-status">
      {record ? editedByLine(record) : "As scanned"}
    </p>
  );
}

function RevertButton({
  editor,
  layer,
  kind,
  id,
  label,
}: {
  readonly editor: PlanEditor;
  readonly layer: PlanEditLayer;
  readonly kind: "wall" | "opening" | "room";
  readonly id: string;
  readonly label: string;
}): ReactNode {
  const record = recordFor(layer.state.records, kind, id);
  if (!record || record.action === "add") return null;
  return (
    <button type="button" className="floor-plan__tool" onClick={() => editor.revert(kind, id, `revert ${label}`)}>
      Revert to scan
    </button>
  );
}

function scannedLength(layer: PlanEditLayer, id: string): number | undefined {
  for (const room of capturedRooms(layer.capture)) {
    const wall = (room.walls ?? []).find((w) => w.identifier === id);
    if (wall) {
      const line = surfacePlanLine(wall);
      return line ? planLineLengthM(line) : undefined;
    }
  }
  return undefined;
}

function WallCard({
  wall,
  room,
  editor,
  layer,
}: {
  readonly wall: PlanSegment;
  readonly room: PlanRoom;
  readonly editor: PlanEditor;
  readonly layer: PlanEditLayer;
}): ReactNode {
  const [anchor, setAnchor] = useState<LengthAnchor>("start");
  const record = recordFor(layer.state.records, "wall", wall.id);
  const scanned = record?.action === "add" ? undefined : scannedLength(layer, wall.id);
  const heightM = wall.vertical ? wall.vertical.topY - wall.vertical.bottomY : undefined;
  return (
    <div className="floor-plan__edit-card" data-testid="plan-edit-wall">
      <p className="floor-plan__edit-name">
        <strong>{wall.label}</strong> <span className="muted">· {room.label}</span>
      </p>
      <Status layer={layer} kind="wall" id={wall.id} />
      {scanned !== undefined && Math.abs(scanned - wall.lengthM) > 0.0005 ? (
        <p className="floor-plan__edit-status muted">Scanned at {formatLength(scanned, "m")}.</p>
      ) : null}
      <div className="floor-plan__edit-grid">
        <NumberField
          label="Length"
          unit="m"
          value={wall.lengthM}
          min={L.wallLengthM.min}
          max={L.wallLengthM.max}
          testId="plan-edit-length"
          onCommit={(m) => editor.setWallLength(wall.id, m, anchor)}
        />
        <label className="floor-plan__field floor-plan__edit-field">
          <span>Keep fixed</span>
          <select value={anchor} onChange={(event) => setAnchor(event.target.value as LengthAnchor)} aria-label="Which end stays put when the length changes">
            <option value="start">End A</option>
            <option value="centre">The middle</option>
            <option value="end">End B</option>
          </select>
        </label>
        <NumberField
          label="Thickness"
          unit="mm"
          value={wall.thicknessM}
          scale={1000}
          digits={0}
          placeholder="Not set"
          min={L.wallThicknessM.min}
          max={L.wallThicknessM.max}
          testId="plan-edit-thickness"
          onCommit={(m) => editor.setWallProps(wall.id, { thicknessM: m })}
        />
        <NumberField
          label="Height"
          unit="m"
          value={heightM}
          min={L.wallHeightM.min}
          max={L.wallHeightM.max}
          testId="plan-edit-height"
          onCommit={(m) => editor.setWallProps(wall.id, { heightM: m })}
        />
      </div>
      <div className="floor-plan__edit-actions">
        <button type="button" className="floor-plan__tool" onClick={() => editor.straighten(wall.id)} title="Turn it square to the house (S)">
          Straighten
        </button>
        <button type="button" className="floor-plan__tool floor-plan__tool--danger" onClick={() => editor.remove("wall", wall.id)} title="Delete (Del)">
          Delete
        </button>
        <RevertButton editor={editor} layer={layer} kind="wall" id={wall.id} label={wall.label} />
      </div>
    </div>
  );
}

function OpeningCard({
  opening,
  host,
  editor,
  layer,
}: {
  readonly opening: PlanSegment;
  readonly host: PlanSegment | undefined;
  readonly editor: PlanEditor;
  readonly layer: PlanEditLayer;
}): ReactNode {
  const heightM = opening.vertical ? opening.vertical.topY - opening.vertical.bottomY : undefined;
  const sill = openingSillM(opening, host);
  return (
    <div className="floor-plan__edit-card" data-testid="plan-edit-opening">
      <p className="floor-plan__edit-name">
        <strong>{opening.label}</strong>
        {host ? <span className="muted"> · in {host.label}</span> : null}
      </p>
      <Status layer={layer} kind="opening" id={opening.id} />
      <div className="floor-plan__edit-grid">
        <NumberField
          label="Width"
          unit="m"
          value={opening.lengthM}
          min={L.openingWidthM.min}
          max={L.openingWidthM.max}
          testId="plan-edit-width"
          onCommit={(m) => editor.setOpening(opening.id, { widthM: m })}
        />
        <NumberField
          label="Height"
          unit="m"
          value={heightM}
          min={L.openingHeightM.min}
          max={L.openingHeightM.max}
          onCommit={(m) => editor.setOpening(opening.id, { heightM: m })}
        />
        <NumberField
          label={opening.kind === "window" ? "Sill" : "Off the floor"}
          unit="m"
          value={sill}
          min={L.sillHeightM.min}
          max={L.sillHeightM.max}
          placeholder="Unknown"
          onCommit={(m) => editor.setOpening(opening.id, { sillHeightM: m })}
        />
        {host ? (
          <NumberField
            label="Centre from end A"
            unit="m"
            value={openingOffsetM(opening, host)}
            min={L.offsetM.min}
            max={L.offsetM.max}
            testId="plan-edit-offset"
            onCommit={(m) => editor.setOpening(opening.id, { offsetM: m })}
          />
        ) : null}
      </div>
      <div className="floor-plan__edit-actions">
        <button type="button" className="floor-plan__tool floor-plan__tool--danger" onClick={() => editor.remove("opening", opening.id)} title="Delete (Del)">
          Delete
        </button>
        <RevertButton editor={editor} layer={layer} kind="opening" id={opening.id} label={opening.label} />
      </div>
    </div>
  );
}

function RoomCard({
  room,
  rooms,
  editor,
  layer,
}: {
  readonly room: PlanRoom;
  /** This storey's rooms: what it can be merged with. */
  readonly rooms: readonly PlanRoom[];
  readonly editor: PlanEditor;
  readonly layer: PlanEditLayer;
}): ReactNode {
  const [mergeWith, setMergeWith] = useState("");
  if (!room.externalRef) return null;
  const ref = room.externalRef;
  const others = rooms.filter((r) => r.externalRef && r.externalRef !== ref);
  return (
    <div className="floor-plan__edit-card" data-testid="plan-edit-room">
      <p className="floor-plan__edit-name">
        <strong>{room.label}</strong> <span className="muted">· {room.storyLabel}</span>
      </p>
      <Status layer={layer} kind="room" id={ref} />
      <label className="floor-plan__field floor-plan__edit-field">
        <span>Room name</span>
        <input
          data-testid="plan-edit-room-name"
          key={room.label}
          defaultValue={room.label}
          maxLength={L.roomLabelLength}
          onBlur={(event) => {
            if (event.target.value.trim() !== room.label) editor.setRoomLabel(ref, event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") {
              event.currentTarget.value = room.label;
              event.currentTarget.blur();
            }
          }}
        />
      </label>
      {others.length > 0 ? (
        <div className="floor-plan__field floor-plan__edit-field">
          <label>
            <span>Merge with</span>
            <select
              data-testid="plan-edit-merge-with"
              value={mergeWith}
              onChange={(event) => setMergeWith(event.target.value)}
            >
              <option value="">Choose a room…</option>
              {others.map((r) => (
                <option key={r.externalRef} value={r.externalRef!}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="floor-plan__tool"
            disabled={mergeWith === ""}
            onClick={() => editor.mergeRoom(ref, mergeWith, `${room.label} into ${others.find((r) => r.externalRef === mergeWith)?.label ?? "a room"}`)}
          >
            Merge
          </button>
          <p className="muted small">
            {room.label} joins the room you choose, which keeps its name. The wall between them goes, with any
            door or opening in it.
          </p>
        </div>
      ) : null}
      <div className="floor-plan__edit-actions">
        <RevertButton editor={editor} layer={layer} kind="room" id={ref} label="room name" />
      </div>
    </div>
  );
}

function Skipped({ skipped, editor }: { readonly skipped: readonly SkippedPlanEdit[]; readonly editor: PlanEditor }): ReactNode {
  return (
    <div className="floor-plan__edit-skipped" role="note">
      <p className="floor-plan__edit-subtitle">
        {skipped.length === 1 ? "A correction no longer applies" : `${skipped.length} corrections no longer apply`}
      </p>
      <ul className="floor-plan__panel-list">
        {skipped.map((s) => (
          <li key={planEditKey(s.edit)} className="floor-plan__edit-row">
            <span className="small">
              {s.edit.targetKind} {s.edit.targetId.slice(0, 8)}: {s.reason}
            </span>
            <button type="button" className="floor-plan__link" onClick={() => editor.revert(s.edit.targetKind, s.edit.targetId, "remove a stale correction")}>
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
